import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { PlayArrow as RunIcon } from '@mui/icons-material';

interface PreviewRailShellProps {
  /** The run button's label. */
  runLabel: string;
  /** The run button's label while running; the run label when absent. */
  runningLabel?: string;
  running: boolean;
  runDisabled: boolean;
  onRun: () => void;
  /** False shows the no-sample state until there is a result. */
  hasSample: boolean;
  noSampleMessage: string;
  /** Shown with a sample and nothing run yet. */
  idleMessage: string;
  error: string | null;
  onClearError?: () => void;
  hasResult: boolean;
  /** Shown above the states, whatever the state. */
  intro?: React.ReactNode;
  /** Controls in the header beside the run button, such as an expand button. */
  headerActions?: React.ReactNode;
  testId?: string;
  /** The result, rendered once there is one and nothing is running. */
  children?: React.ReactNode;
}

/** The right-hand preview column of an editor: a header with a run button, the idle, loading and error states, and the result. */
const PreviewRailShell: React.FC<PreviewRailShellProps> = ({
  runLabel,
  runningLabel,
  running,
  runDisabled,
  onRun,
  hasSample,
  noSampleMessage,
  idleMessage,
  error,
  onClearError,
  hasResult,
  intro,
  headerActions,
  testId,
  children,
}) => (
  <Box
    data-testid={testId}
    sx={{
      width: 340,
      flexShrink: 0,
      borderLeft: 1,
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'background.default',
    }}
  >
    {/* Rail header */}
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
        Preview
      </Typography>
      {headerActions}
      <Button
        variant="contained"
        size="small"
        startIcon={running ? <CircularProgress size={14} color="inherit" /> : <RunIcon />}
        onClick={onRun}
        disabled={running || runDisabled}
        disableElevation
      >
        {running ? (runningLabel ?? runLabel) : runLabel}
      </Button>
    </Box>

    {/* Rail body */}
    <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {intro}

      {!hasSample && !hasResult && !running && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1, color: 'text.disabled' }}>
          <RunIcon sx={{ fontSize: 40, opacity: 0.3 }} />
          <Typography variant="body2" color="text.disabled" textAlign="center">
            {noSampleMessage}
          </Typography>
        </Box>
      )}

      {hasSample && !hasResult && !running && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
          <RunIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {idleMessage}
          </Typography>
        </Box>
      )}

      {running && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1.5 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Running…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={onClearError} sx={{ fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}

      {hasResult && !running && children}
    </Box>
  </Box>
);

export default PreviewRailShell;
